import React, { Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp, Wrench, Coffee, Copy, Check, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import { useRequestStore } from '../store/useRequestStore';
import { createLogger } from '../lib/logger';

// ============================================
// ERROR FALLBACK COMPONENT (Functional)
// ============================================

interface ErrorFallbackProps {
  error: Error;
  resetError?: () => void;
  errorInfo?: ErrorInfo | null;
}

function ErrorFallback({ error, resetError, errorInfo }: ErrorFallbackProps) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = React.useState(false);
  const [copiedRefId, setCopiedRefId] = React.useState(false);
  const isDev = import.meta.env.DEV;

  // Get request context for server errors
  const requestState = useRequestStore();
  const isServerError = requestState.lastErrorStatus !== null &&
    requestState.lastErrorStatus >= 500 &&
    requestState.lastErrorStatus < 600;
  const requestId = requestState.lastRequestId;

  // Copy reference ID to clipboard
  const handleCopyRefId = async () => {
    if (requestId) {
      try {
        await navigator.clipboard.writeText(requestId);
        setCopiedRefId(true);
        setTimeout(() => setCopiedRefId(false), 2000);
      } catch {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = requestId;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopiedRefId(true);
        setTimeout(() => setCopiedRefId(false), 2000);
      }
    }
  };

  // Generate mailto link for support
  const supportEmail = 'admin@yimburundi.com';
  const emailSubject = encodeURIComponent(`Error Report - Reference ID: ${requestId || 'N/A'}`);
  const emailBody = encodeURIComponent(
    `Hi YIM Support Team,\n\n` +
    `I encountered an error while using the application.\n\n` +
    `Reference ID: ${requestId || 'N/A'}\n` +
    `Error: ${error.message}\n` +
    `Time: ${new Date().toISOString()}\n` +
    `URL: ${typeof window !== 'undefined' ? window.location.href : 'N/A'}\n\n` +
    `Please help me resolve this issue.\n\n` +
    `Thank you.`
  );
  const mailtoLink = `mailto:${supportEmail}?subject=${emailSubject}&body=${emailBody}`;

  // Check if this is a DOM manipulation error (likely from browser extension)
  const isDomError = error.message.includes('removeChild') ||
    error.message.includes('insertBefore') ||
    error.message.includes('appendChild');

  // Check if this is a network/API error
  const isNetworkError = error.message.includes('Network') ||
    error.message.includes('fetch') ||
    error.message.includes('Failed to fetch') ||
    error.message.includes('ECONNREFUSED');

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleReset = () => {
    if (resetError) {
      resetError();
    } else {
      handleRefresh();
    }
  };

  // Server Error (5xx) - Display with Request ID for traceability
  if (isServerError && requestId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#1a0f2e] to-slate-900 flex items-center justify-center p-4">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-1/4 left-1/4 w-64 h-64 bg-red-500/10 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FBBF24]/10 rounded-full blur-3xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative max-w-lg w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', damping: 12 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-red-500/20 to-red-600/10 backdrop-blur-sm border border-red-500/20">
              <AlertTriangle className="w-16 h-16 text-red-400" />
            </div>
          </motion.div>

          <h1 className="text-3xl font-bold text-white mb-4">
            {t('errors.boundary.serverErrorTitle', 'Internal Error Occurred')}
          </h1>

          <p className="text-red-400 font-medium mb-4">
            {t('errors.boundary.serverErrorSubtitle', 'Something went wrong on our end')}
          </p>

          <p className="text-slate-600 mb-6">
            {t('errors.boundary.serverErrorMessage', 'Our team has been notified. Please contact support if this issue persists.')}
          </p>

          {/* Reference ID Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8"
          >
            <p className="text-slate-600 text-sm mb-2">
              {t('errors.boundary.referenceId', 'Reference ID')}
            </p>
            <div className="flex items-center justify-center gap-3">
              <code className="text-[#FBBF24] font-mono text-lg bg-slate-800/50 px-4 py-2 rounded-lg">
                {requestId}
              </code>
              <button
                onClick={handleCopyRefId}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                title={t('common.copy', 'Copy')}
              >
                {copiedRefId ? (
                  <Check className="w-5 h-5 text-green-400" />
                ) : (
                  <Copy className="w-5 h-5 text-slate-600" />
                )}
              </button>
            </div>
            <p className="text-slate-600 text-xs mt-3">
              {t('errors.boundary.shareRefId', 'Please share this ID when contacting support')}
            </p>
          </motion.div>

          {/* Contact Info */}
          <p className="text-slate-600 text-sm mb-6">
            {t('errors.boundary.contactAdmin', 'Please contact')} <a href={`mailto:${supportEmail}`} className="text-[#FBBF24] hover:underline">{supportEmail}</a> {t('errors.boundary.withRefId', 'with Reference ID:')}
            <br />
            <span className="font-mono text-[#FBBF24]">{requestId}</span>
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleReset}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-slate-900 rounded-full font-semibold shadow-lg"
            >
              <RefreshCw className="w-4 h-4" />
              {t('common.tryAgain', 'Try Again')}
            </motion.button>

            <motion.a
              href={mailtoLink}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-semibold border border-white/20"
            >
              <Mail className="w-4 h-4" />
              {t('errors.boundary.contactSupport', 'Contact Support')}
            </motion.a>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGoHome}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-semibold border border-white/20"
            >
              <Home className="w-4 h-4" />
              {t('errors.boundary.goHome', 'Home')}
            </motion.button>
          </div>

          <p className="mt-8 text-slate-600 text-xs">
            <span className="text-[#FBBF24]">YIM</span> Burundi
          </p>
        </motion.div>
      </div>
    );
  }

  // Premium maintenance-style error page for network errors
  if (isNetworkError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#0a1628] to-slate-900 flex items-center justify-center p-4">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#1E40AF]/10 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FBBF24]/10 rounded-full blur-3xl"
            animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 10, repeat: Infinity }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative max-w-lg w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', damping: 12 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10">
              <Coffee className="w-16 h-16 text-[#FBBF24]" />
            </div>
          </motion.div>

          <h1 className="text-3xl font-bold text-white mb-4">
            {t('maintenance.connectionTitle', 'Connection interrupted')}
          </h1>

          <p className="text-[#FBBF24] font-medium mb-4">
            {t('maintenance.connectionSubtitle', 'The server is temporarily unavailable')}
          </p>

          <p className="text-slate-600 mb-8">
            {t('maintenance.connectionDescription', "We're working to restore service. Please wait or try again.")}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleReset}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] text-slate-900 rounded-full font-semibold shadow-lg"
            >
              <RefreshCw className="w-4 h-4" />
              {t('common.tryAgain', 'Try again')}
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleGoHome}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-semibold border border-white/20"
            >
              <Home className="w-4 h-4" />
              {t('errors.boundary.goHome', 'Go to Home')}
            </motion.button>
          </div>

          <p className="mt-8 text-slate-600 text-xs">
            <span className="text-[#FBBF24]">YIM</span> Burundi
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4">
      <div className="max-w-lg w-full">
        {/* Error Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                {isDomError ? <Wrench className="w-8 h-8" /> : <AlertTriangle className="w-8 h-8" />}
              </div>
              <div>
                <h1 className="text-xl font-bold">{t('errors.boundary.title')}</h1>
                <p className="text-white/80 text-sm mt-1">
                  {isDomError ? t('errors.boundary.domConflict') : t('errors.boundary.subtitle')}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Friendly message */}
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              {isDomError
                ? t('errors.boundary.domConflictMessage')
                : t('errors.boundary.message')
              }
            </p>

            {/* Suggestion for DOM errors */}
            {isDomError && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-6">
                <p className="text-amber-800 dark:text-amber-200 text-sm">
                  <strong>{t('errors.boundary.suggestion')}:</strong>{' '}
                  {t('errors.boundary.domSuggestion')}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleReset}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                {t('errors.boundary.tryAgain')}
              </button>
              <button
                onClick={handleGoHome}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-lg font-medium transition-colors"
              >
                <Home className="w-4 h-4" />
                {t('errors.boundary.goHome')}
              </button>
            </div>

            {/* Technical Details (Dev only or expandable) */}
            {(isDev || error.message) && (
              <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-600">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-600 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                >
                  {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {t('errors.boundary.technicalDetails')}
                </button>

                {showDetails && (
                  <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg overflow-auto max-h-48">
                    <p className="text-xs font-mono text-red-600 dark:text-red-400 break-all">
                      {error.name}: {error.message}
                    </p>
                    {isDev && errorInfo?.componentStack && (
                      <pre className="mt-2 text-xs font-mono text-slate-600 dark:text-slate-600 whitespace-pre-wrap">
                        {errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-600 dark:text-slate-600 mt-6">
          {t('errors.boundary.persistsMessage')}{' '}
          <a href="mailto:contact@yimburundi.com" className="text-primary-600 hover:underline">
            {t('errors.boundary.contactSupport')}
          </a>
        </p>
      </div>
    </div>
  );
}

// ============================================
// ERROR BOUNDARY CLASS COMPONENT
// ============================================

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Report error to backend for admin dashboard visibility
    const logger = createLogger('ErrorBoundary');
    logger.reportComponentError(error, errorInfo.componentStack || '').catch(() => {
      // Silently fail - don't break the error boundary
    });
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        if (typeof this.props.fallback === 'function') {
          return this.props.fallback(this.state.error);
        }
        return this.props.fallback;
      }

      // Use default error fallback
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

// ============================================
// ROUTE ERROR ELEMENT
// For use with React Router's errorElement prop
// ============================================

export function RouteErrorBoundary() {
  const { t } = useTranslation();

  // Create a generic error for route errors
  const error = new Error(t('errors.boundary.routeError'));

  return <ErrorFallback error={error} />;
}

export default ErrorBoundary;
